/**
 * Circuit lap selector — Driver tab, ONLY when event.type === 'Circuit'
 * (handover §3 + mockup tpl-drv "Laps" section).
 *
 * One 5-column grid, NEWEST FIRST left→right (operator request
 * 2026-06-11: the freshest lap is what you look for; oldest laps sink).
 * Row 1 = [live chip when recording][most recent laps…][All ▾] with the
 * expander in the TOP-RIGHT cell; "All" extends the SAME grid below
 * (cell size/gap identical) with the older laps continuing in
 * descending order, max-height ~160px scroll. For 10 laps + expander:
 *   row 1     L10 L9 L8 L7 [All ▾]
 *   expanded  L6 L5 L4 L3 L2 / L1
 *
 * Flags: best = fastest so far (green dot + green time); slow = ≥3%
 * over the median (red dot + red time); legend in the section header.
 * Circuit-Engine σ is NOT rendered inside the chips (field report:
 * time + ±σ overflows the 1/5 cell) — the selected lap's σ shows in
 * the DriverTab telemetry "Timing" cell instead; here it only enriches
 * the hover tooltip.
 *
 * An off-window selection echoes on the All chip ("L4 ✓") so it is
 * never invisible. Picking a past lap flips the pane's video badge to
 * REPLAY · Ln at the orchestrator level; the live chip restores LIVE.
 */

import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Lap } from '../../services/fleet';

export const formatLapTime = (ms: number): string => {
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  // Sub-minute laps drop the "0:" (motorsport convention) — the chip
  // cell is 1/5 of the rail and must also fit the ±σ suffix.
  if (minutes === 0) return seconds.toFixed(1);
  return `${minutes}:${seconds.toFixed(1).padStart(4, '0')}`;
};

/** Circuit Engine 1σ chip: 107 → "±0.1s", 1240 → "±1.2s". */
export const formatSigma = (sigmaMs: number): string =>
  `±${(sigmaMs / 1000).toFixed(1)}s`;

export const sigmaToneClass = (confidence: Lap['confidence']): string => {
  switch (confidence) {
    case 'high':
      return 'text-status-online';
    case 'med':
      return 'text-status-warning';
    default: // 'low' or missing confidence — muted
      return 'text-fg-secondary';
  }
};

/** How many completed-lap chips fit on row 1 next to the All chip. */
const WINDOW_WITH_LIVE = 3; // [All][L a][L b][L c][live]
const WINDOW_NO_LIVE = 4; // [All][L a][L b][L c][L d]

const LapChip: React.FC<{
  label: string;
  time: React.ReactNode;
  selected?: boolean;
  best?: boolean;
  slow?: boolean;
  live?: boolean;
  onClick: () => void;
  title?: string;
}> = ({ label, time, selected = false, best = false, slow = false, live = false, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={cn(
      'flex min-w-0 flex-col items-center justify-center gap-px rounded-sm border border-border-subtle bg-bg-surface px-1 py-[5px] transition-colors hover:border-border-mid',
      best && 'border-[var(--status-online-border)]',
      selected && 'border-accent bg-[var(--accent-primary-bg)]',
    )}
  >
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[9px] font-bold tracking-wide',
        selected ? 'text-accent' : 'text-fg-muted',
      )}
    >
      {label}
      {best && <span className="h-[5px] w-[5px] rounded-full bg-status-online" />}
      {slow && !best && <span className="h-[5px] w-[5px] rounded-full bg-status-error" />}
      {live && (
        <span className="h-[5px] w-[5px] animate-pulse rounded-full bg-status-error" />
      )}
    </span>
    <span
      className={cn(
        'whitespace-nowrap font-mono text-[11px] font-semibold',
        best ? 'text-status-online' : slow ? 'text-status-error' : 'text-fg',
      )}
    >
      {time}
    </span>
  </button>
);

export const LapSelector: React.FC<{
  /** Chronologically sorted laps (useEventLaps order). */
  laps: Lap[];
  /** Device currently recording (live page + active session) → live chip. */
  live: boolean;
  /** Index into `laps` of the selected past lap; null = live / none. */
  selectedIdx: number | null;
  onPickLap: (lap: Lap, idx: number) => void;
  onPickLive: () => void;
}> = ({ laps, live, selectedIdx, onPickLap, onPickLive }) => {
  const [expanded, setExpanded] = useState(false);

  const { bestIdx, slowSet } = useMemo(() => {
    const empty = { bestIdx: -1, slowSet: new Set<number>() };
    // Rank only clean line-to-line laps; the out-lap (lap_number 0) is a
    // partial whose start is offset from the line, so it never competes
    // for best/slow. Indices stay relative to the full `laps` array so
    // they line up with renderLap below.
    const clean = laps
      .map((l, i) => ({ l, i }))
      .filter(({ l }) => l.lap_number >= 1);
    if (clean.length === 0) return empty;
    const best = clean.reduce((b, c) => (c.l.duration_ms < b.l.duration_ms ? c : b));
    const sorted = clean.map(({ l }) => l.duration_ms).sort((a, b) => a - b);
    const median = sorted[Math.floor((sorted.length - 1) / 2)];
    const slow = new Set<number>();
    clean.forEach(({ l, i }) => {
      if (median > 0 && l.duration_ms >= median * 1.03) slow.add(i);
    });
    return { bestIdx: clean.length > 1 ? best.i : -1, slowSet: slow };
  }, [laps]);

  // Out-lap (lap_number 0) renders as an "OUT" chip but is not a
  // line-to-line lap, so the count badge and the live chip's next-lap
  // label both count only clean laps.
  const cleanCount = laps.filter((l) => l.lap_number >= 1).length;
  const lapLabel = (l: Lap) => (l.lap_number === 0 ? 'OUT' : `L${l.lap_number}`);

  const windowSize = live ? WINDOW_WITH_LIVE : WINDOW_NO_LIVE;
  const olderCount = Math.max(0, laps.length - windowSize);
  // Newest first: row 1 renders the recent window REVERSED (L10 L9 …),
  // and the expanded grid continues descending (… L2 / L1). `laps`
  // itself stays chronological — indices (chip labels, selection) are
  // unchanged; only render order flips.
  const recentDesc = laps
    .map((lap, idx) => ({ lap, idx }))
    .slice(olderCount)
    .reverse();
  const olderDesc = laps
    .map((lap, idx) => ({ lap, idx }))
    .slice(0, olderCount)
    .reverse();
  const selectionInOlder = selectedIdx != null && selectedIdx < olderCount;

  const renderLap = (lap: Lap, idx: number) => (
    <LapChip
      key={`${lap.session_id}-${lap.lap_number}`}
      label={lapLabel(lap)}
      time={formatLapTime(lap.duration_ms)}
      selected={selectedIdx === idx}
      best={idx === bestIdx}
      slow={slowSet.has(idx)}
      onClick={() => onPickLap(lap, idx)}
      title={
        lap.sigma_ms != null
          ? `Play this lap · ${formatSigma(lap.sigma_ms)} GPS timing 1σ${lap.method ? ` (${lap.method})` : ''}`
          : 'Play this lap'
      }
    />
  );

  return (
    <div className="border-b border-border-subtle p-3.5">
      <div className="mb-2.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-fg-muted">
        <span>Laps</span>
        <span className="inline-flex items-center gap-2 font-medium normal-case tracking-normal">
          <span className="inline-flex items-center gap-1">
            <i className="inline-block h-[5px] w-[5px] rounded-full bg-status-online" />
            best
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="inline-block h-[5px] w-[5px] rounded-full bg-status-error" />
            slow
          </span>
          <b className="font-mono font-semibold text-fg-secondary">
            {cleanCount}
            {live ? ' + live' : ''}
          </b>
        </span>
      </div>
      {laps.length === 0 && !live ? (
        <div className="py-1 font-mono text-[10px] text-fg-muted">no laps yet</div>
      ) : (
        <>
          <div className="grid grid-cols-5 gap-1.5">
            {/* Most recent first: the live (current) lap is the newest
                thing there is, so it takes the top-left cell. */}
            {live && (
              <LapChip
                label={`L${cleanCount + 1}`}
                time="live"
                live
                selected={selectedIdx == null}
                onClick={onPickLive}
                title="Back to the live feed"
              />
            )}
            {recentDesc.map(({ lap, idx }) => renderLap(lap, idx))}
            {olderCount > 0 && (
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
                className={cn(
                  'flex min-w-0 flex-col items-center justify-center gap-px rounded-sm border border-dashed border-border-subtle bg-bg-surface px-1 py-[5px] transition-colors hover:border-border-mid',
                  selectionInOlder && 'border-solid border-accent bg-[var(--accent-primary-bg)]',
                )}
                title="Show older laps"
              >
                <span
                  className={cn(
                    'text-[9px] font-bold tracking-wide',
                    selectionInOlder ? 'text-accent' : 'text-fg-muted',
                  )}
                >
                  {/* Off-window selection echoes here so it's never invisible */}
                  {selectionInOlder ? `${lapLabel(laps[selectedIdx!])} ✓` : `L1–${olderCount}`}
                </span>
                <ChevronDown
                  className={cn(
                    'size-3.5 text-fg-muted transition-transform',
                    expanded && 'rotate-180',
                  )}
                />
              </button>
            )}
          </div>
          {expanded && olderDesc.length > 0 && (
            <div className="mt-1.5 grid max-h-[160px] grid-cols-5 gap-1.5 overflow-y-auto pr-0.5">
              {olderDesc.map(({ lap, idx }) => renderLap(lap, idx))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LapSelector;
